import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {DiseaseExtended, TreatmentRequestDiagnosisValidationSchema} from '@/features/treatment-requests/types'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSearchDisease} from '@/features/treatment-requests/components/input-search-disease/InputSearchDisease'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {DiseasesGroupsRadioButtons} from '@/features/treatment-requests/components/diseases-groups-radio-buttons/DiseasesGroupsRadioButtons'
import {DiseasesGroupsSelectors} from '@/features/treatment-requests/components/diseases-groups-selectors/DiseasesGroupsSelectors'
import {InputDate} from '@/components/commons/input-date-legacy/InputDate'
import dayjs from 'dayjs'
import {PatientAgeRange} from '@/types'
import {Text} from '@/components/ui/text/Text'
import {useEffect} from 'react'
import {ObjectValues} from '@/utilities/helpers'
import {getDiagnosisSelectionVersion, getFirstMappedCode} from '@/features/projects/diagnosisVersion'
import {Diagnosis} from '@/features/projects/types'

export const TreatmentRequestDiagnosisForm = ({
    serviceType
}: {
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
}) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestDiagnosisValidationSchema>()
    const diseases = useWatch({control: form.control, name: 'diseases'})
    const scores = useWatch({control: form.control, name: 'scores'})
    const icd10DiagnosisCode = useWatch({control: form.control, name: 'icd10DiagnosisCode'})

    const isIcd9Selectable = getDiagnosisSelectionVersion() == 'ICD9'

    const {fields, remove, replace} = useFieldArray({
        name: 'diseases',
        control: form.control,
        keyName: 'key'
    })

    const handleSelectDiseaseGroups = (value: DiseaseExtended) => {
        const parents = []
        let currentParent = value.parent

        while (currentParent) {
            parents.unshift(currentParent)
            currentParent = currentParent.parent
        }

        replace([...parents, value])
    }

    useEffect(() => {
        if (
            diseases &&
            diseases.some(disease => disease.name != 'I° Gruppo') &&
            scores &&
            ObjectValues(scores).some(type => typeof type == 'string' && type == 'subtype')
        ) {
            form.setValue(
                'scores',
                scores.filter(score => score.type != 'subtype')
            )
        }
    }, [diseases, scores])

    return (
        <Flexbox direction="column" gap={6} fullWidth>
            <Grid columns={6} fullWidth>
                <GridItem spanColumn={4}>
                    <Controller
                        control={form.control}
                        name="diseases"
                        render={() => (
                            <InputSearchDisease
                                serviceType={serviceType}
                                placeholder={t('treatment_requests:search_diseases_groups')}
                                onChange={value => {
                                    if (!!value.parent?.id && value.parent.id != value.id) {
                                        handleSelectDiseaseGroups(value)
                                    } else {
                                        replace([value])
                                    }
                                }}
                                onClear={() => replace([])}
                            />
                        )}
                    />
                </GridItem>
            </Grid>

            <Flexbox direction="column" gap={4} fullWidth>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('treatment_requests:select_disease_group_label')}
                </Text>
                <Flexbox direction="column" gap={5} fullWidth>
                    <DiseasesGroupsRadioButtons serviceType={serviceType} onChange={replace} />

                    {fields?.length > 0 && (
                        <DiseasesGroupsSelectors
                            fields={fields}
                            serviceType={serviceType}
                            onChange={handleSelectDiseaseGroups}
                            onClear={index =>
                                fields.map((_, fieldIndex) => (fieldIndex > index ? remove(fieldIndex) : null))
                            }
                        />
                    )}

                    {fields.length > 0 && (
                        <Controller
                            control={form.control}
                            name="diseaseOnsetDate"
                            render={({field}) => (
                                <InputDate
                                    label={t('treatment_requests:disease_onset_date')}
                                    isOptional
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={form.formState.errors.diseaseOnsetDate?.message}
                                    maxDate={dayjs().toDate()}
                                    numMonths={1}
                                    selectedDates={field.value ? [field.value] : []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    )}
                </Flexbox>
            </Flexbox>

            <Grid columns={6} fullWidth gap={2.5}>
                <Controller
                    control={form.control}
                    name="diagnosisCode"
                    render={({field}) => (
                        <GridItem spanColumn={3}>
                            <InputSelectPathology
                                label={t('treatment_requests:icd9_code')}
                                placeholder={t('treatment_requests:icd9_code_placeholder')}
                                value={field.value}
                                onChange={value => {
                                    field.onChange(value)
                                    // The transcoding only proposes an ICD-10 while that field is
                                    // still empty: a code already picked is never overwritten.
                                    if (!icd10DiagnosisCode) {
                                        form.setValue('icd10DiagnosisCode', getFirstMappedCode(value), {
                                            shouldDirty: true
                                        })
                                    }
                                }}
                                disabled={!isIcd9Selectable}
                                isClearable
                                version="ICD9"
                            />
                        </GridItem>
                    )}
                />
                <Controller
                    control={form.control}
                    name="icd10DiagnosisCode"
                    render={({field}) => (
                        <GridItem spanColumn={3}>
                            <InputSelectPathology
                                label={t('treatment_requests:icd10_code')}
                                placeholder={t('treatment_requests:icd10_code_placeholder')}
                                value={field.value}
                                // Picked from the whole ICD-10 list and stored on its own: it never
                                // alters the ICD-9 beside it.
                                onChange={field.onChange as (value: Diagnosis | null) => void}
                                isClearable
                                version="ICD10"
                            />
                        </GridItem>
                    )}
                />
            </Grid>
        </Flexbox>
    )
}
